import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import Spinner from 'app/components/Spinner';

import ContactFormItemFactory from './ContactFormItemFactory';
import useContactFormLogic from '../hooks/useContactFormLogic';
import { useContactFormState } from '../stores/useContactFormState';
interface ContactFormContentProps {
  onClose?: () => void;
  className?: string;
}

export default function ContactFormContent({ onClose, className = '' }: ContactFormContentProps) {
  const { t } = useTranslation();
  const { contactFormSlug } = useContactFormState();

  const {
    isFetching,
    isPending,
    currentForm,
    currentPage,
    submitted,
    isValid,
    hasErrors,
    handleFormSubmit,
    setValidity,
    isComplete,
    dirtyFields,
    setFieldDirty,
  } = useContactFormLogic({ onClose, contactFormSlug });

  if (isFetching) {
    return (
      <div className={`flex grow w-full items-center justify-center ${className}`}>
        <Spinner />
      </div>
    );
  }

  if (!currentForm) {
    return null;
  }

  return (
    <div className={className}>
      <div className="sr-only">
        <p
          role="alert"
          aria-live="polite"
        >
          {hasErrors ? t('contact_form_has_errors') : ''}
        </p>
      </div>
      <form
        id="contact-form"
        key={currentForm.ref_id}
        noValidate
        onSubmit={handleFormSubmit}
        ref={(form) => form && setValidity(form.checkValidity())}
        onChange={({ currentTarget: form }) => setValidity(form.checkValidity())}
        className="flex flex-col gap-4 h-full"
      >
        <AnimatePresence mode="wait">
          <motion.div
            className="flex flex-col gap-4 h-full"
            initial={currentPage === 1 ? false : { x: 50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -50, opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            {currentForm.items.map((item) => (
              <ContactFormItemFactory
                key={item.ref_id}
                item={item}
                isSubmitted={submitted}
                hasErrors={hasErrors}
                isValid={isValid}
                isFetching={isPending}
                buttonVariant="default"
                isComplete={isComplete}
                showSpacer={true}
                dirtyFields={dirtyFields}
                setFieldDirty={setFieldDirty}
                contactFormSlug={contactFormSlug}
              />
            ))}
            <div className="h-2 shrink-0" />
          </motion.div>
        </AnimatePresence>
      </form>
    </div>
  );
}
